// Sync read / async-but-fire-and-forget write helpers for firm-wide settings.
// Backed by /api/data/settings (firm_settings table in Supabase) and a
// module-level cache populated by FirmSettingsProvider on boot.
//
// USAGE:
//   import { getFirmSetting, setFirmSetting, patchFirmSettings } from './firmSettings';
//
//   const overrides = getFirmSetting('plOverridesGlobal', null);
//   setFirmSetting('forecastScenario', 'aggressive');         // fire-and-forget
//   await patchFirmSettings({ scenarios, scenarioFloor: 0.1 }); // batched

import { apiFetch } from './apiFetch';
import { getFirmSettingsCache, patchFirmSettingsCache } from './firmSettingsStore';

export function getFirmSetting(key, defaultValue) {
  const cache = getFirmSettingsCache();
  return cache[key] !== undefined ? cache[key] : defaultValue;
}

// Track listeners that want to know when a settings write fails so the
// UI can surface a toast/banner instead of silently dropping the change.
// This is what made the May 13-Jun 2 LinkedIn post incident invisible:
// PATCH errors only logged to console while the optimistic cache happily
// showed the new value, so the user had no idea the save hadn't landed.
const _writeErrorListeners = new Set();
export function onFirmSettingsWriteError(fn) {
  _writeErrorListeners.add(fn);
  return () => _writeErrorListeners.delete(fn);
}
function reportWriteError(detail) {
  console.error('[firmSettings] PATCH failed:', detail);
  for (const fn of _writeErrorListeners) {
    try { fn(detail); } catch (e) { console.error('[firmSettings] listener threw:', e); }
  }
}

export async function patchFirmSettings(keys) {
  if (!keys || typeof keys !== 'object') return;
  // Optimistic local update so re-renders see the new value immediately.
  patchFirmSettingsCache(keys);
  try {
    const res = await apiFetch('/api/data/settings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keys }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      reportWriteError({
        kind: 'http',
        status: res.status,
        body: data,
        keys: Object.keys(keys),
      });
    }
  } catch (err) {
    reportWriteError({ kind: 'network', message: err.message, keys: Object.keys(keys) });
  }
}

export function setFirmSetting(key, value) {
  // Sync API surface — fire and forget.
  patchFirmSettings({ [key]: value });
}
